import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { differenceInMinutes, isAfter, isEqual as isSameDate } from 'date-fns'
import i18next from 'i18next'
import { groupBy, sumBy } from 'lodash-es'
import { Badge } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { getRestaurant, getRestaurantIfMany } from 'state/Restaurants'
import { Alerts, endOfToday, FMT, formatDistanceStrict } from 'utils'
import { store } from '../_store'
import API from '../API'
import { getSettings } from '../Settings'
import Model from './Model'
import OrderItem from './OrderItem'
import TimezonedModel from './TimezonedModel'

function getSetting(key) {
	return getSettings(store.getState())[key]
}

//TODO: move those surcharge fields into the Restaurant model
/** @typedef {Object} RestaurantDetails
 * @property {string} per_order_surcharge_name
 * @property {number} per_order_surcharge_type
 * @property {number} per_order_surcharge
 * @property {string} delivery_fee_name
 */
/** @typedef {Object} GroupOrder
 * @property {number} id
 * @property {number} who_will_pay
 * @property {number} payment_method
 * @property {number[]} component_order_ids
 *
 * @typedef {Object} CustomOrderType
 * @property {number} id
 * @property {number} display_name
 * @property {BaseOrderType} base_type
 * @property {boolean} show_custom_checkout_field
 * @property {string} custom_checkout_field_label
 *
 * @typedef {'PICKUP' | 'DELIVERY' | 'DINEIN'} BaseOrderType
 */

/** @class */ //just so we separate the definition from the @typedef above
export default class Order extends TimezonedModel {

	//region Properties
	/**
	 * It will be a fake object if the order isn't even present in Redux.
	 * E.g. opening a link for an order outside the current date range.
	 * @type boolean */ fake

	/** @type int */ number
	/** This is the BASE order type - either pickup, dinein or delivery. The custom order name is at {@link Order.type.name}.
	 * @type BaseOrderType
	 */ order_type
	/** @type string */ customer_id
	/** @type string */ customer_name
	/** @type string */ customer_current_phone
	/** @type boolean */ is_open
	/** @type boolean */ is_cancelled
	/** @type boolean */ is_completed
	/** @type boolean */ is_customer_vip
	/** @type int */ payment_method
	/**
	 * Prefer to use {@link Order.type.name}
	 * @private
	 * @type {string|null}
	 */ order_type_display_name
	/** @type string|null */ custom_payment_type_name
	/** @type boolean */ is_captured
	/** @type {'PAID IN FULL' | 'PROCESS PAYMENT' | 'COLLECT PAYMENT'} */ payment_status
	/** @type boolean */ should_show_card_info
	/** @type string */ house_account_id
	/** @type string */ special_instructions
	/** @type string */ driver_notes
	/** @type int */ restaurant_id
	/** @type string */ restaurant_name

	/** @type {null|CustomOrderType} */ custom_order_type
	/** @type {[string,string][]} */ custom_fields
	/** @type {[string,string][]} */ fees //FIXME this type is wrong

	/** @type string */ tax_label
	/** @type float */ tax_rate
	/** @type int */ tax_amount
	/** @type string */ tax_label_2
	/** @type float */ tax_rate_2
	/** @type int */ tax_amount_2
	/** @type int */ coupon_id
	/** @type string */ coupon_name
	/** @type string */ coupon_code
	/** @type int */ coupon_amount
	/** @type boolean */ coupon_can_be_used_for_all
	/** @type int */ delivery_charge_amount
	/** @type int */ cc_fee
	/** @type RestaurantDetails */ restaurant
	/** @type int */ additional_fee
	/** @type int
	 * @readonly */ subtotal
	/** @type int */ total_price
	/** @type {null|GroupOrder} */ group_order
	/** @type int */ group_order_id
	/** @type int */ group_order_invitee_id
	/** @type {string|number|null} */ tip_text
	/** @type {string|null} */ coupon_free_item
	/** @type {string|null} */ bags_type
	/** @type int */ bags_fee
	/** @type boolean */ delivery_charge_is_taxable
	/** @type boolean */ additional_fee_taxable
	/** @type boolean */ is_bag_fee_taxable
	/** @type string */ time_slot_name

	/** @type Date */ ready_at
	/** @type Date */ created_at
	/** @type Date */ modified_at
	/** If zero, means the "future order" feature is off
	 * @type {int} */ future_order_minutes
	/** @type boolean */ is_asap
	/** If true, the "ready at" time should be hidden for ASAP orders
	 * @type boolean */ asap_no_time
	/** @type boolean */ is_flagged
	/** @type number */ itemsCount
	/** EVERY Order Item, regardless if it was originally ordered by the customer, it's an adjustment to an existing item or a whole new item. This shouldn't be used to create viewable lists, as it's not organized for the end-users.
	 * @type {OrderItem[]} */ #allItems
	/** What shows in the main items list - doesn't include any adjustment made to the main ordered items, but does include new items added via adjustments.
	 * @type {OrderItem[]|null} */ items
	/** Only items originally ordered by the customer - no items included through adjustments.
	 * @type {OrderItem[]|null} */ originalItems
	/** Only changes made to the other items, made via adjustments - these won't show directly on the items main list (but under their affected item), nor affect the item count.
	 * @type {OrderItem[]} */ itemAdjustments

	/** @type ShipdayDetails */ shipday
	/** @type boolean */ can_deliver_with_shipday
	/** @type boolean */ deliver_by_ourselves
	/** @type boolean */ is_on_springroll

	/** Even if this is true, the value might be cached locally, and it depends on time: it becomes false 24h after ready_at.
	 * @see canBeAdjusted()
	 * @type boolean */ #can_be_adjusted
	/** This is used solely to calculate the maximum amount of tip that can be given upon adjustments.
	 * @type {number} */ tippable_amount

	/**
	 * One of {@link Order.STATUS}
	 * @type string
	 */ statusSlug

	/** @property {boolean} */ third_parties_loaded

	/** Small, reusable components for order details */
	comp = {}
	//endregion

	/**
	 * Key of constants: status "slugs"
	 * @see STATUS_TXT
	 * @see Used at {@link Order.ICONS}
	 * @return {{ ACTIVE: string,	COMPLETED: string,	CANCELED: string,	FUTURE: string }}
	 */
	static get STATUS() {
		return Object.freeze({
			ACTIVE   : 'open', //active: backend naming; open: restaurant naming
			COMPLETED: 'completed',
			CANCELED : 'canceled',
			FUTURE   : 'future'
		})
	}

	/**
	 * Actually humanized name of order statuses.
	 * @see STATUS
	 * @return {Readonly<string>}
	 */
	static get STATUS_TXT() {
		return Object.freeze({
			ACTIVE   : i18next.t('open', { context: 'orders' }),
			COMPLETED: i18next.t('completed', { context: 'orders' }),
			CANCELED : i18next.t('canceled', { context: 'orders' }),
			FUTURE   : i18next.t('future', { context: 'orders' }),
		})
	}

	/**
	 * All icons used to identify different order statuses. Keys are values of {@link STATUS}.
	 * @returns {Readonly<{[OrderSTATUS: string]: {icon: string, flip: undefined|string}}>}
	 */
	static get ICONS() {
		return Object.freeze({
			[Order.STATUS.ACTIVE]   : { icon: 'thumbtack' },
			[Order.STATUS.COMPLETED]: { icon: 'check' },
			[Order.STATUS.CANCELED] : { icon: 'window-close' },
			[Order.STATUS.FUTURE]   : { icon: 'history', flip: 'horizontal' },
		})
	}

	//FIXME: seems to be called a thousand of times, for each order, in the Order Details screen?????
	constructor({ ready_at, created_at, modified_at, order_items, shipday, shipday_third_party, can_be_adjusted, ...attrs }) {
		super(attrs)

		//TODO: this is SO. DIRTY. Please, move this and that weird array-with-boolean from the selector into some status fields inside this object... Are there private props in JS? Something like Order._loaded (either PLACEHOLDER, LOADED or PARTIAL) that could be checked by isLoaded() and isPlaceholder() (use "placeholder" instead of "fake", "loaded" instead of "status" and "loaded" instead of "complete", as that's used for the actual order status)
		if (this.fake) { //skip further fields in case of fake objects
			return this
		} else {
			this.fake = false
		}

		if (created_at) {
			this.created_at = this.newDate(created_at)
		}
		this.modified_at   = this.newDate(modified_at)
		this.ready_at      = this.newDate(ready_at)
		this.tip_text      = this.tip_text && this.tip_text.indexOf('$') >= 0? FMT.demoney(this.tip_text) : this.tip_text
		this.custom_fields = Object.entries(attrs.custom_fields || {})
		this.fees          = Object.entries(attrs.fees || {})

		//calculated props - don't add any time-related props, since these aren't pure, they're more suitable in getters
		this.#allItems       = order_items? OrderItem.map(order_items) : []
		const splitItems     = groupBy(this.#allItems, i => (i.is_open_item && i.isSubAdjustment)? 'adjustments' : 'main')
		this.items           = splitItems.main || []
		this.itemAdjustments = splitItems.adjustments || []
		this.originalItems   = this.items.filter(i => !i.is_open_item)
		this.itemsCount      = sumBy(this.items, 'quantity')

		this.restaurant ||= {} //this could be empty when the object is coming from the Orders List and is used to pre-fill the Order Details screen

		this.shipday = new ShipdayDetails({ ...shipday, third_party_name: shipday_third_party })

		this.#can_be_adjusted = can_be_adjusted

		if (this.group_order && this.group_order.who_will_pay == 1) { //CONSTANTS.GROUP_ORDER.PAID_CREATOR
			this.payment_method = this.group_order_invitee_id < 1? this.group_order.payment_method : 'GROUP_CREATOR'
		}

		this.statusSlug = this.is_cancelled?
			Order.STATUS.CANCELED : (this.is_completed?
				Order.STATUS.COMPLETED :
				Order.STATUS.ACTIVE)

		this.comp = {
			isAsap   : () => this.is_asap? <Badge variant="warning">{i18next.t('ASAP')}</Badge> : null,
			isFlagged: () => this.is_flagged? <Icon className="text-warning" icon="pennant"/> : null,
			pmtStatus: ({ showPaid = false }) => (
				!this.is_captured? <Badge variant="danger">{i18next.t('Not paid')}</Badge> :
					(showPaid? <Badge variant="success">{i18next.t('Paid')}</Badge> :
						null)
			),

			dueTime: ({ withIcon = false }) => {
				const { enableCompleted } = getSettings(store.getState()) //TODO: is this setting really needed?
				const icon = withIcon? <this.timeStatus.Icon fixedWidth className="mr-1 d-none d-sm-inline"/> : null
				return (
					this.is_due || this.is_future?
						<div className={`d-inline-block ${enableCompleted? 'text-uppercase' : 'text-bold-reset'}`}>
							{this.is_due? <>{icon}<span>{i18next.t('Past due')}</span></> : <>{icon}<span>{i18next.t('Future order')}</span></>}
						</div> :
						formatDistanceStrict(this.ready_at, this.newDate(), { addSuffix: true, roundingMethod: 'floor' })
				)
			},

			/**
			 * @param {TFunction} t
			 * @param {boolean} defaultToOrderType
			 * @return JSX.Element
			 */
			deliveryBadge: ({ t, defaultToOrderType = false }) => (
				this.deliver_by_ourselves? <Badge variant="outline-dark">{t('In-house', { context: '...driver' })}</Badge> :
					(this.shipday.has_driver? <Badge variant="dark">{this.shipday?.third_party_name}</Badge> :
						(defaultToOrderType? <strong>{this.type.name}</strong> :
							null))
			),
		}
	}

	/** @type {Restaurant} */
	useRestaurant() {
		return useRestaurant.apply(this)
	}
	/**
	 * This only checks this order's adjustments window; to be sure the user is allowed to make adjustments, check {@link User.useCanAdjustOrders()} - you can even pass an order object to it, as it optionally calls this method.
	 * @returns {boolean}
	 */
	canBeAdjusted() {
		return this.#can_be_adjusted && this.diff_hours >= -24
	}

	/**
	 * If third-party delivery is enabled but not requested yet, nor was it tagged for in-house delivery yet
	 * @param account {Account}
	 * @returns {boolean}
	 */
	canToggleInHouse(account) {
		return !!(account.deliver_by_ourselves_enabled
			&& this.can_deliver_with_shipday && this.shipday.is_requestable
			&& !this.deliver_by_ourselves
		)
	}

	/**
	 * Retrieves all adjustments related to this specific item
	 * @param item {number|OrderItem} Either the ID or the item object
	 * @returns {OrderItem[]}
	 */
	getAdjustmentsFor(item) {
		const itemId = typeof item == 'number'? item : item.id
		return this.itemAdjustments.filter(i => i.adjusted_order_item_id == itemId)
	}

	/**
	 * The sum of the originally ordered items - no adjustments whatsoever.
	 * @return number
	 */
	get total_original_items() { return OrderItem.totalForList(this.originalItems) }

	/**
	 * The sum of the everything listed in the order - adjustments and all -, also known as subtotal or total pre-tax.
	 * @return number
	 */
	get total_all_items() { return OrderItem.totalForList(this.#allItems) }

	/**
	 * A state selector to grab the restaurant related to this order.
	 * @example useSelector(order.getRestaurant)
	 * @return {(function(object): Restaurant)}
	 */
	get getRestaurant() {
		return getRestaurant(this.restaurant_id)
	}

	/**
	 * A state selector to grab the restaurant of this order - but only if the account has more than one (active or not).
	 * @example useSelector(order.getRestaurantIfNeedsDistinction)
	 * @return {(function(object): Restaurant)|null}
	 */
	get getRestaurantIfNeedsDistinction() {
		return getRestaurantIfMany(this.restaurant_id)
	}

	/**
	 * Details about the order type, including an Icon component :)
	 * @return {FMT.orderType}
	 */
	get type() {
		return FMT.orderType(this.order_type, this.order_type_display_name)
	}

	/** @returns {string} */
	get tip_formatted() {
		//if that replace() shows weird for you, it's the actual char for &nbsp;
		return typeof this.tip_text == 'string'? this.tip_text.replace(' ', ' ') : FMT.money(this.tip_text)
	}

	/**
	 * @returns {null|number} Returns null if the tip is non-numeric - that's DIFFERENT from a zeroed tip!
	 */
	get tip_value() {
		return typeof this.tip_text == 'string'? null : this.tip_text
	}

	get is_delivery() { return this.order_type == 'DELIVERY' }

	get is_paid() { return this.payment_status == 'PAID IN FULL' }

	/**
	 * Encapsulation of the is due / is next / is future status, with the respective icon, if any.
	 * @return {{class: string|null, Icon: (function(attr): JSX.Element|null)}}
	 */
	get timeStatus() {
		const select = (due, next, future) => (
			this.is_due && getSetting('enableCompleted'))? due
				: (this.is_next? next
					: (this.is_future? future
						: null))

		return {
			class: select('text-order-due', 'text-order-next', 'text-order-future'),
			Icon : attr => {
				const icon = select(
					{ icon: 'alarm-exclamation' },
					{ icon: ['far','clock'] },
					Order.ICONS[Order.STATUS.FUTURE]
				)
				return icon? <Icon {...icon} {...attr}/> : null
			}
		}
	}

	// /** @return {Restaurant|null} */
	// get restaurant() {
	// 	return getRestaurantById(this.restaurant_id)(store.getState())
	// }

	get diff_minutes() { return differenceInMinutes(this.ready_at, this.newDate()) }
	get diff_hours() { return this.diff_minutes / 60 }

	/** @return {boolean} */
	get is_due() { return this.diff_minutes < 0 }

	/**
	 * Checks if the order falls into the "future order" rule, as configured in the Admin. Null means that's disabled, and no "future order" warning should be issued.
	 * @see {is_considered_future} If you're looking for a way to sort orders in the UI.
	 * @return {?boolean} Returns null if the "future order" feature is disabled.
	 */
	get is_future() { return this.future_order_minutes > 0? (this.diff_minutes >= this.future_order_minutes) : null }

	/**
	 * Whether this order shouldn't be considered for immediate preparation. This is _unrelated_ to the "future order"
	 * feature - if it's disabled, it still considers an order as "future" if it's for tomorrow on.
	 * @see {is_future} governs the "future order" warnings. This method is to be used for UI purposes only.
	 * @return {boolean}
	 */
	get is_considered_future() { return this.is_future || (this.is_future === null && this.not_for_today) }

	get not_for_today() { return isAfter(this.ready_at, endOfToday()) }

	get is_next() { return this.diff_minutes < getSetting('doableTime') }

	readyAtFormatted(forceDate, longAsap = false) {
		if (this.is_asap && this.asap_no_time) {
			return longAsap? i18next.t('As soon as possible') : <this.comp.isAsap/>
		}
		if (this.time_slot_name) {
			return FMT.date(this.ready_at, { forceDate, noTime: true }) + ' @ ' + this.time_slot_name
		}
		return FMT.date(this.ready_at, { forceDate })
	}

	static isEqual(a, b) { return a.id == b.id && isSameDate(a.modified_at, b.modified_at) }
	/**
	 * Returns the state selector for a specific Order (which final result will include a "completion" boolean, stating if it's the complete order, a half-loaded one, or a placeholder -fake- object).
	 * @param id {number}
	 * @returns {function(state):[Order, boolean]}
	 * @todo see the TODO above the this.fake check in the constructor!
	 */
	static fromState(id) {
		return ({ orders: { list, current } }) => {
			if (current.id && current.id == id) {
				return [new Order(current), false]
			} else {
				//find the real basic data, or create a fake order object so some checks are easier
				let data = list.find(o => o.id == id) || { id, fake: true }
				return [new Order(data), true]
			}
		}
	}

	/**
	 * Checks if the given time frame allows notification for this order.
	 * @param newOrderWhen {number} Comes from the Settings object
	 * @param futureOrderAlert {boolean} Comes from the Settings object
	 */
	shouldNotify(newOrderWhen, futureOrderAlert) {
		if (this.not_for_today && futureOrderAlert) {
			return true
		}

		const notifyWhen = newOrderWhen * 60
		if (notifyWhen > 0 && this.diff_minutes > notifyWhen) {
			console.debug(`Skipping notification for #${this.id} (${this.diff_minutes} > ${notifyWhen})`, this)
			return false
		}

		return true
	}

	/**
	 * @param forceImage
	 * @returns {Promise<{ data: Blob }>} The axios response object
	 */
	async getReceipt(forceImage = false) {
		try { // noinspection JSValidateTypes
			return await API.getAndHandle(
				`order/${this.id}/receipt`,
				[500 /* big 💩 */, 502 /* trouble generating the image */],
				{ responseType: 'blob', params: { forceImage:true } }
			)
		} catch(e) {
			Alerts.danger(i18next.t('There was an error trying to format the order #{{num}} for receipt printing.', { num: this.number })+' '+i18next.t('CONTACT_TRY_AGAIN'))
		}
	}
}

function useRestaurant() {
		if (!this.restaurant_id) {
			throw new Error('Cannot find the restaurant details for an incomplete order.')
		}

		return useSelector(getRestaurant(this.restaurant_id))
}

class ShipdayDetails {
	/** @type number */ id
	/** @type {'REQUESTED'|'STARTED'|'PICKEDUP'|'DELIVERED'} */ status
	/** @type string */ third_party_name
	/** @type string */ tracking_url

	constructor({ id, status, third_party_name, tracking_url }) {
		this.id               = id
		this.status           = status
		this.third_party_name = third_party_name
		this.tracking_url     = tracking_url
	}

	get has_driver() { return !!this.third_party_name }
	get is_trackable() { return !!this.tracking_url }
	get is_cancelable() { return this.status == 'REQUESTED' }
	get is_requestable() { return !this.is_trackable && this.status != 'DELIVERED' }
}

export class PaymentDetails extends Model {
	/** @type string */ number
	/** @type string */ expires
	/** @type string */ name
	/** @type string */ zip
	/** @type string */ ccv
	/** @type string */ disclaimer
}
